import moment from 'moment'
import momentDurationFormatSetup from 'moment-duration-format'

momentDurationFormatSetup(moment)

const DEFAULT_LANG = 'en'
const DEFAULT_LOCALE = `${DEFAULT_LANG}-US`

export const getLang = locale => {
  if (locale) {
    const [lang, region] = locale.split('-')
    return region ? lang : undefined
  }
}

export const loadLocaleData = (locale = DEFAULT_LOCALE) =>
  Promise.all([loadMomentLocale(locale.toLowerCase()), loadIntlPolyfill(locale)])

/**
 * Load Intl polyfill and locale data if needed
 * @param locale `BCP 47` language tag
 * @return {Promise<*>}
 */
export const loadIntlPolyfill = locale =>
  global.Intl
    ? Promise.resolve(Intl)
    : import(/* webpackChunkName: "intl-polyfill" */ 'intl').then(() =>
        import(/* webpackChunkName: "intl-polyfill-locale-data" */ `intl/locale-data/jsonp/${locale}.js`)
      )

/**
 * Load moment.js locale file and set default locale.
 * @param locale `BCP 47` language tag
 * @return {Promise<*>}
 */
export const loadMomentLocale = locale =>
  !locale
    ? moment.locale(DEFAULT_LANG)
    : importMomentFile(locale)
        .then(() => moment.locale(locale))
        .catch(() => loadMomentLocale(getLang(locale)))

/**
 * Load rc-calendar locale file. If no file is found for locale in parameter, default locale is retrieved
 * @param locale
 * @return {Promise<T | never>}
 */
export const loadRCCalendarLocale = locale =>
  importRCCalendarFile(locale).catch(() => importRCCalendarFile(DEFAULT_LOCALE))

/**
 * Hack-ish method to determine if time is in 12 hours format for a locale
 * @param locale
 * @return {boolean}
 */
export const use12Hours = locale => {
  const now = moment()
  if (locale) {
    now.locale(locale)
  }
  return now.localeData()._longDateFormat.LTS.substr(-1) === 'A'
}

/**
 * dynamic import rc-calendar locale file
 * @param filename
 * @return {Promise<*>|*}
 */
const importRCCalendarFile = filename =>
  import(/* webpackChunkName: "rc-calendar-locale-data" */ `rc-calendar/lib/locale/${filename.replace('-', '_')}.js`)

/**
 * dynamic import moment locale file
 * @param filename
 * @return {Promise<*>|*}
 */
const importMomentFile = filename => import(/* webpackChunkName: "moment-locale-data" */ `moment/locale/${filename}.js`)

/**
 * Transform a duration in milliseconds into the format 'hh:mm:ss'
 * @param value
 * @return {string}
 */
export const formatDuration = value => moment.duration(value, 'milliseconds').format('hh:mm:ss', { trim: false })
